import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, orderBy, addDoc, doc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../../shared/storage/firebase';
import { Squad, User } from '../../../types';
import { PACKS } from '../../../data/packs';
import { resolveProgramTermId } from '../../../shared/terms/programTerms';
import LinkStudentsModal from './LinkStudentsModal';
import ParentInfoModal from './ParentInfoModal';
import ConfirmDialog from './ConfirmDialog';
import '../styles/ManageParents.css';

interface LinkedStudent { id: string; displayName: string; pickupPreference?: any; isInSquad?: boolean; }
interface School { id: string; name: string; slug: string; partnerId: string }
interface Parent extends User {
  linkedStudents: LinkedStudent[];
  hasNextSeasonLinkedChild?: boolean;
  hasFallSeasonLinkedChild?: boolean;
}

const PICKUP_LABELS: Record<string, string> = {
  aftercare: 'Aftercare',
  guardian_pickup: 'Guardian Pick-up',
  bike_walk: 'Bike/Walk',
  other: 'Other',
};

function formatPickupPreference(pp: any): string {
  if (!pp) return '';

  // Back-compat: some records may store as string[]
  const options: string[] = Array.isArray(pp)
    ? pp
    : Array.isArray(pp?.options)
      ? pp.options
      : [];

  const cleaned = options
    .filter((v) => typeof v === 'string')
    .map((v) => v.trim())
    .filter(Boolean);

  const unique = Array.from(new Set(cleaned));
  if (unique.length === 0) return '';

  const labels = unique.map((k) => PICKUP_LABELS[k] || k);
  const otherText = typeof pp?.otherText === 'string' ? pp.otherText.trim() : '';
  if (unique.includes('other') && otherText) {
    return `${labels.join(', ')} (Other: ${otherText})`;
  }
  return labels.join(', ');
}

const ManageParents: React.FC = () => {
  const [parents, setParents] = useState<Parent[]>([]);
  const [squads, setSquads] = useState<Squad[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedSquadId, setSelectedSquadId] = useState('');
  const [linkedCountFilter, setLinkedCountFilter] = useState('');
  const [parentForLinking, setParentForLinking] = useState<Parent | null>(null);
  const [creatingParent, setCreatingParent] = useState<Parent | null>(null);
  const [confirm, setConfirm] = useState<{
    open: boolean;
    title?: string;
    message?: string;
    onConfirm?: () => void;
  }>({ open: false });
  const [schoolOptions, setSchoolOptions] = useState<School[]>([]);
  const [editingCell, setEditingCell] = useState<{
    id: string;
    field: 'firstName' | 'lastName' | 'email' | 'phone' | 'school';
  } | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingPickupStudentId, setEditingPickupStudentId] = useState<string | null>(null);
  const [pickupDraftOptions, setPickupDraftOptions] = useState<string[]>([]);
  const [pickupDraftOtherText, setPickupDraftOtherText] = useState<string>('');
  const [savingPickup, setSavingPickup] = useState(false);
  const [manualEmailModalOpen, setManualEmailModalOpen] = useState(false);
  const [manualEmailSquadId, setManualEmailSquadId] = useState('');
  const [manualEmailSending, setManualEmailSending] = useState(false);
  const [manualEmailResult, setManualEmailResult] = useState<string | null>(null);
  const [convertingParentId, setConvertingParentId] = useState<string | null>(null);

  // "Next season" is Winter for this rollout.
  const NEXT_SEASON_KEY = 'winter';
  const FALL_SEASON_KEY = 'fall';

  useEffect(() => {
    loadParents();
  }, []);

  const visibleSquads = useMemo(() => {
    const filtered = selectedSchool
      ? squads.filter(s => String((s as any).locationId || '') === selectedSchool)
      : squads;

    // Sort: archived squads to the bottom, then by title
    return [...filtered].sort((a, b) => {
      const aIsArchived = (a as any).status === 'archived';
      const bIsArchived = (b as any).status === 'archived';
      if (aIsArchived && !bIsArchived) return 1;
      if (!aIsArchived && bIsArchived) return -1;
      return String(a.title || '').localeCompare(String(b.title || ''));
    });
  }, [squads, selectedSchool]);

  useEffect(() => {
    if (selectedSquadId && !visibleSquads.some(s => s.id === selectedSquadId)) {
      setSelectedSquadId('');
    }
  }, [selectedSquadId, visibleSquads]);

  const selectedSquadStudentIdSet = useMemo(() => {
    if (!selectedSquadId) return null;
    const squad = squads.find(s => s.id === selectedSquadId);
    if (!squad) return null;
    const ids: string[] = Array.isArray((squad as any).studentIds) ? (squad as any).studentIds : [];
    return new Set(ids);
  }, [selectedSquadId, squads]);

  useEffect(() => {
    const loadSchools = async () => {
      try {
        const schoolsRef = collection(db, 'publicLocations');
        const q = query(schoolsRef, orderBy('locationName'));
        const snap = await getDocs(q);
        const list: School[] = snap.docs.map(d => ({
          id: d.id,
          name: `${(d.data() as any).locationName || d.id} — ${(d.data() as any).partnerName || ''}`,
          slug: String((d.data() as any).locationSlug || ''),
          partnerId: String((d.data() as any).partnerId || ''),
        }));
        setSchoolOptions(list);
      } catch (e) {
        setSchoolOptions([]);
      }
    };
    loadSchools();
  }, []);

  const loadParents = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get all users from Firestore
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
      
      // Filter for parents only
      let parentsData = usersData.filter(user => user.role === 'parent');

      // Build map of students for quick name lookups
      const studentsMap: Record<string, { displayName?: string; email?: string; pickupPreference?: any }> = {};
      usersData.forEach(u => {
        if ((u as any).role === 'student') {
          studentsMap[(u as any).id as string] = {
            displayName: u.displayName as string | undefined,
            email: (u as any).email as string | undefined,
            pickupPreference: (u as any).pickupPreference,
          };
        }
      });

      // Get linked students for each parent
      const parentStudentLinksRef = collection(db, 'parentStudentLinks');
      const linksSnapshot = await getDocs(parentStudentLinksRef);
      const linksData = linksSnapshot.docs.map(doc => doc.data());

      // Build list of student ids per parent
      const parentIdToStudents: { [parentId: string]: string[] } = {};
      linksData.forEach((link: any) => {
        const list = parentIdToStudents[link.parentId] || [];
        if (!list.includes(link.studentId)) list.push(link.studentId);
        parentIdToStudents[link.parentId] = list;
      });

      // Aggregate parent login counts
      const loginSessionsRef = collection(db, 'parentLoginSessions');
      const loginSessionsSnap = await getDocs(loginSessionsRef);
      const loginCountByParent: Record<string, number> = {};
      loginSessionsSnap.docs.forEach(doc => {
        const data: any = doc.data();
        const pid = data?.parentId;
        if (pid) {
          loginCountByParent[pid] = (loginCountByParent[pid] || 0) + 1;
        }
      });

      // Determine which students are enrolled in Fall / Winter programs.
      // Back-compat:
      // - Prefer stable `termId` (e.g. "winter2026")
      // - Fallback to legacy `term` label substring matching
      const winterStudentIdSet = new Set<string>();
      const fallStudentIdSet = new Set<string>();
      // Track all students currently in any active (non-archived) squad
      const activeSquadStudentIdSet = new Set<string>();
      try {
        const squadsRef = collection(db, 'squads');
        const squadsSnap = await getDocs(squadsRef);
        const squadsData = squadsSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) } as Squad));
        setSquads(squadsData);
        squadsData.forEach((s) => {
          const stableTermId = resolveProgramTermId({ termId: (s as any).termId, legacyLabel: (s as any).term });
          const termId = String(stableTermId || '').toLowerCase();
          const legacyTerm = String((s as any).term || '').toLowerCase();
          const ids: string[] = Array.isArray((s as any).studentIds) ? (s as any).studentIds : [];
          const isWinter = (termId && termId.startsWith('winter')) || legacyTerm.includes(NEXT_SEASON_KEY);
          const isFall = (termId && termId.startsWith('fall')) || legacyTerm.includes(FALL_SEASON_KEY);
          if (isWinter) ids.forEach(id => winterStudentIdSet.add(id));
          if (isFall) ids.forEach(id => fallStudentIdSet.add(id));
          // Add to active squad set if squad is not archived
          const isArchived = (s as any).status === 'archived';
          if (!isArchived) {
            ids.forEach(id => activeSquadStudentIdSet.add(id));
          }
        });
      } catch (e) {
        setSquads([]);
        // Best-effort; if squads can't be loaded, we just won't show the flag
        console.warn('Failed to load squads for next season check', e);
      }

      // Helper: derive photo consent from childConsents map if parent-level missing
      const derivePhotoConsent = (p: any): 'accept' | 'deny' | undefined => {
        const parentLevel = p?.consents?.photoRelease?.value as 'accept' | 'deny' | undefined;
        if (parentLevel) return parentLevel;
        const cc = p?.childConsents as Record<string, any> | undefined;
        if (!cc || typeof cc !== 'object') return undefined;
        let anyAccept = false;
        let anyDeny = false;
        for (const k of Object.keys(cc)) {
          const v = cc[k]?.photoRelease as any;
          if (v === 'accept' || v === true) anyAccept = true;
          if (v === 'deny' || v === false) anyDeny = true;
        }
        if (anyAccept) return 'accept';
        if (anyDeny) return 'deny';
        return undefined;
      };

      // Attach linked student names and compute display fallbacks
      let parentsWithLinks: Parent[] = parentsData.map(parent => {
        const pid = (parent as any).id as string;
        let linked = (parentIdToStudents[pid] || []).map((id: string) => ({
          id,
          displayName: studentsMap[id]?.displayName || 'Student',
          pickupPreference: studentsMap[id]?.pickupPreference,
          isInSquad: activeSquadStudentIdSet.has(id),
        }));
        // Fallback: if no link docs but parent.studentIds exist, show those
        if (linked.length === 0 && Array.isArray((parent as any).studentIds) && (parent as any).studentIds.length > 0) {
          linked = ((parent as any).studentIds as string[]).map(id => ({
            id,
            displayName: studentsMap[id]?.displayName || 'Student',
            pickupPreference: studentsMap[id]?.pickupPreference,
            isInSquad: activeSquadStudentIdSet.has(id),
          }));
        }

        // Compute school fallback from first studentInfo entry
        const firstStudentInfo = Array.isArray((parent as any).studentInfo) && (parent as any).studentInfo.length > 0
          ? (parent as any).studentInfo[0]
          : undefined;
        const computedSchoolSlug = (parent as any).locationId || (parent as any).schoolSlug || firstStudentInfo?.school || '';

        // Compute consent summary
        const computedPhotoConsent = derivePhotoConsent(parent as any);
        const hasNextSeasonLinkedChild = linked.some(s => winterStudentIdSet.has(s.id));
        const hasFallSeasonLinkedChild = linked.some(s => fallStudentIdSet.has(s.id));

        const pickupPreferenceSummary = (() => {
          if (!linked || linked.length === 0) return '—';
          const parts = linked.map((s) => {
            const formatted = formatPickupPreference(s.pickupPreference);
            return `${s.displayName}: ${formatted || '—'}`;
          });
          return parts.join(' | ');
        })();

        return {
          ...(parent as any),
          // Derived fields for display only
          primaryPhone: Array.isArray((parent as any).phones) && (parent as any).phones.length > 0
            ? (parent as any).phones[0]
            : (parent as any).parentPhone,
          loginCount: loginCountByParent[pid] || 0,
          linkedStudents: linked,
          computedSchoolSlug,
          computedPhotoConsent,
          hasNextSeasonLinkedChild,
          hasFallSeasonLinkedChild,
          pickupPreferenceSummary,
        } as any;
      });

      // Do not apply filters here; filter locally to avoid reloading on every keystroke

      // Sort by creation date (newest first)
      parentsWithLinks.sort((a, b) => {
        const dateA = a.createdAt ? 
          (a.createdAt instanceof Date ? a.createdAt : 
           typeof a.createdAt.seconds === 'number' ? new Date(a.createdAt.seconds * 1000) : 
           new Date()) : new Date(0);
        const dateB = b.createdAt ? 
          (b.createdAt instanceof Date ? b.createdAt : 
           typeof b.createdAt.seconds === 'number' ? new Date(b.createdAt.seconds * 1000) : 
           new Date()) : new Date(0);
        return dateB.getTime() - dateA.getTime();
      });

      setParents(parentsWithLinks);

    } catch (err) {
      console.error('Error loading parents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load parents');
    } finally {
      setLoading(false);
    }
  };

  const beginEditPickup = (studentId: string, current: any) => {
    setEditingPickupStudentId(studentId);
    const pp: any = current;
    const options: string[] = Array.isArray(pp)
      ? pp
      : Array.isArray(pp?.options)
        ? pp.options
        : [];
    setPickupDraftOptions(
      options
        .filter((v) => typeof v === 'string')
        .map((v) => v.trim())
        .filter(Boolean)
    );
    setPickupDraftOtherText(typeof pp?.otherText === 'string' ? pp.otherText : '');
  };

  const cancelEditPickup = () => {
    setEditingPickupStudentId(null);
    setPickupDraftOptions([]);
    setPickupDraftOtherText('');
    setSavingPickup(false);
  };

  const togglePickupDraftOption = (key: string) => {
    setPickupDraftOptions((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      if (!next.includes('other')) setPickupDraftOtherText('');
      return next;
    });
  };

  const recomputePickupSummary = (linked: LinkedStudent[]): string => {
    if (!linked || linked.length === 0) return '—';
    const parts = linked.map((s) => {
      const formatted = formatPickupPreference(s.pickupPreference);
      return `${s.displayName}: ${formatted || '—'}`;
    });
    return parts.join(' | ');
  };

  const savePickupForStudent = async (studentId: string) => {
    setSavingPickup(true);
    try {
      const options = Array.from(new Set(pickupDraftOptions.map((v) => String(v).trim()).filter(Boolean)));
      const otherText = pickupDraftOtherText.trim();
      const payload =
        options.length > 0
          ? {
              options,
              ...(options.includes('other') && otherText ? { otherText } : {}),
            }
          : null;

      await updateDoc(doc(db, 'users', studentId), { pickupPreference: payload });

      setParents((prev) =>
        prev.map((p) => {
          const linked = (p.linkedStudents || []).map((ls) =>
            ls.id === studentId ? ({ ...ls, pickupPreference: payload } as any) : ls
          );
          const pickupPreferenceSummary = recomputePickupSummary(linked);
          return { ...(p as any), linkedStudents: linked, pickupPreferenceSummary } as any;
        })
      );

      cancelEditPickup();
    } catch (e) {
      console.error('Failed to update pickup preference', e);
      void window.appAlert('Failed to update pickup preference. Please try again.');
      setSavingPickup(false);
    }
  };

  const handleAddNewParent = async () => {
    try {
      // Create a shell parent user doc first so the modal can load it by ID
      const usersCol = collection(db, 'users');
      const newParentDoc = await addDoc(usersCol, {
        role: 'parent',
        displayName: '',
        email: '',
        studentIds: [],
        status: 'active',
        createdAt: new Date(),
      });

      // Open the modal with the newly created parent
      setCreatingParent({
        id: newParentDoc.id,
        displayName: '',
        email: '',
        role: 'parent',
        selectedMuse: null,
        linkedStudents: [],
      } as any);
    } catch (e) {
      console.error('Failed to create parent:', e);
      void window.appAlert('Failed to create parent. Please try again.');
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'Never';
    const timestamp = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
    return timestamp.toLocaleDateString() + ' ' + timestamp.toLocaleTimeString();
  };

  // Created date removed from table

  const getSchoolDisplayName = (slug: string) => {
    const found = schoolOptions.find(s => s.id === slug);
    return found?.name || slug;
  };

  // Status column removed

  const nameParts = (displayName?: string | null): { first: string; last: string } => {
    if (!displayName) return { first: '', last: '' };
    const parts = displayName.trim().split(/\s+/);
    if (parts.length === 1) return { first: parts[0], last: '' };
    return { first: parts[0], last: parts.slice(1).join(' ') };
  };

  const beginEdit = (parent: Parent, field: 'firstName' | 'lastName' | 'email' | 'phone' | 'school') => {
    let initialValue = '';
    if (field === 'firstName') {
      initialValue = nameParts(parent.displayName).first;
    } else if (field === 'lastName') {
      initialValue = nameParts(parent.displayName).last;
    } else if (field === 'email') {
      initialValue = parent.email || '';
    } else if (field === 'phone') {
      initialValue = ((parent as any).primaryPhone || '') as string;
    } else if (field === 'school') {
      initialValue = ((parent as any).computedSchoolSlug || parent.schoolSlug || '') as string;
    }
    setEditingCell({ id: parent.id, field });
    setEditingValue(initialValue);
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const saveParentField = async (parent: Parent, field: 'firstName' | 'lastName' | 'email' | 'phone' | 'school', value: string) => {
    if (!parent?.id) {
      cancelEdit();
      return;
    }

    const trimmed = value.trim();
    const updates: any = {};
    const localUpdates: any = {};

    if (field === 'firstName' || field === 'lastName') {
      const parts = nameParts(parent.displayName);
      const newFirst = field === 'firstName' ? trimmed : parts.first;
      const newLast = field === 'lastName' ? trimmed : parts.last;
      const newDisplayName = `${newFirst} ${newLast}`.trim();

      // If nothing changed, just exit.
      if ((parent.displayName || '').trim() === newDisplayName) {
        cancelEdit();
        return;
      }

      updates.displayName = newDisplayName;
      localUpdates.displayName = newDisplayName;
    } else if (field === 'email') {
      if ((parent.email || '') === trimmed) {
        cancelEdit();
        return;
      }
      updates.email = trimmed;
      localUpdates.email = trimmed;
    } else if (field === 'phone') {
      const currentRaw = (((parent as any).primaryPhone || '') as string).trim();

      // Allow clearing the phone number entirely
      if (!trimmed) {
        if (!currentRaw) {
          cancelEdit();
          return;
        }

        updates.phones = [];
        updates.parentPhone = null;
        localUpdates.primaryPhone = undefined;
        localUpdates.phones = [];
        localUpdates.parentPhone = undefined;
      } else {
        const digits = trimmed.replace(/[^0-9]/g, '').slice(0, 10);

        // Require exactly 10 digits when edited via admin
        if (digits.length !== 10) {
          void window.appAlert('Phone numbers must have exactly 10 digits, e.g. (312) 863-9096.');
          return; // keep editing; do not persist or exit
        }

        // Format as (XXX) XXX-XXXX
        const len = digits.length;
        let formatted = digits;
        if (len <= 3) {
          formatted = digits;
        } else if (len <= 6) {
          formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        } else {
          formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        }

        const currentDigits = currentRaw.replace(/[^0-9]/g, '');
        if (currentDigits === digits) {
          // No real change in the underlying number
          cancelEdit();
          return;
        }

        // Normalise phone into both phones[0] and parentPhone for compatibility
        updates.phones = [formatted];
        updates.parentPhone = formatted;
        localUpdates.primaryPhone = formatted;
        localUpdates.phones = [formatted];
        localUpdates.parentPhone = formatted;
      }
    } else if (field === 'school') {
      const currentSlug = (((parent as any).computedSchoolSlug || parent.schoolSlug || '') as string).trim();
      if (currentSlug === trimmed) {
        cancelEdit();
        return;
      }

      const selectedLocation = schoolOptions.find(option => option.id === trimmed);
      updates.locationId = selectedLocation?.id || null;
      updates.locationSlug = selectedLocation?.slug || null;
      updates.partnerId = selectedLocation?.partnerId || null;
      localUpdates.locationId = selectedLocation?.id || undefined;
      localUpdates.locationSlug = selectedLocation?.slug || undefined;
      localUpdates.partnerId = selectedLocation?.partnerId || undefined;
      localUpdates.computedSchoolSlug = trimmed || '';
    }

    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      const parentRef = doc(db, 'users', parent.id);
      await updateDoc(parentRef, updates);

      setParents(prev =>
        prev.map(p => (p.id === parent.id ? ({ ...(p as any), ...localUpdates } as Parent) : p))
      );
    } catch (e) {
      console.error('Failed to update parent field', e);
      void window.appAlert('Failed to update parent. Please try again.');
    } finally {
      cancelEdit();
    }
  };

  const handleUnlink = async (parentId: string, studentId: string) => {
    try {
      const { collection, getDocs, query, where, deleteDoc, doc, setDoc, updateDoc, arrayRemove, getDoc } = await import('firebase/firestore');
      const linksRef = collection(db, 'parentStudentLinks');
      const linksQuery = query(linksRef, where('parentId', '==', parentId), where('studentId', '==', studentId));
      const snap = await getDocs(linksQuery);
      if (!snap.empty) {
        await deleteDoc(snap.docs[0].ref);
      }
      // Remove only this parent's id from the student's parentIds
      const studentRef = doc(db, 'users', studentId);
      await updateDoc(studentRef, { parentIds: arrayRemove(parentId) });
      // If that was the last parent, also clear parentEmail
      const updatedStudent = await getDoc(studentRef);
      const remainingParents: string[] = Array.isArray(updatedStudent.data()?.parentIds) ? updatedStudent.data()!.parentIds : [];
      if (!remainingParents || remainingParents.length === 0) {
        await setDoc(studentRef, { parentEmail: null }, { merge: true });
      }
      // Remove student from parent's studentIds
      const parentRef = doc(db, 'users', parentId);
      await updateDoc(parentRef, { studentIds: arrayRemove(studentId) });
      await loadParents();
    } catch (e) {
      void window.appAlert('Failed to unlink student');
    }
  };

  const getUnlinkedStudentNames = (parent: Parent): string[] => {
    const raw = (parent as any).studentInfo as Array<{ firstName?: string; lastName?: string }>|undefined;
    if (!Array.isArray(raw)) return [];
    return raw
      .map(s => `${(s.firstName || '').trim()} ${(s.lastName || '').trim()}`.trim())
      .filter(n => n.length > 0);
  };

  const filteredParents = parents
    .filter(p => {
      if (!selectedSchool) return true;
      return String((p as any).locationId || '') === selectedSchool;
    })
    .filter(p => {
      if (!selectedSquadId) return true;
      if (!selectedSquadStudentIdSet) return true; // if squads didn't load, don't hide all parents
      return p.linkedStudents.some(s => selectedSquadStudentIdSet.has(s.id));
    })
    .filter(p => {
      if (!linkedCountFilter) return true;
      const n = p.linkedStudents.length;
      if (linkedCountFilter === '0') return n === 0;
      const t = parseInt(linkedCountFilter);
      return n >= t;
    })
    .filter(p => {
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return (
        (p.displayName || '').toLowerCase().includes(q) ||
        (p.email || '').toLowerCase().includes(q) ||
        (((p as any).primaryPhone || '') as string).toLowerCase().includes(q) ||
        p.linkedStudents.some(s => (s.displayName || '').toLowerCase().includes(q))
      );
    });

  const handleLaunchParentAccount = (parent: Parent) => {
    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      void window.appAlert('Please allow popups to launch the parent account.');
      return;
    }

    newWindow.document.write(`
      <html>
        <head>
          <title>Launching ${parent.displayName}'s Account...</title>
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              display: flex; 
              justify-content: center; 
              align-items: center; 
              height: 100vh; 
              margin: 0; 
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              text-align: center;
            }
            .loading { animation: spin 1s linear infinite; }
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          </style>
        </head>
        <body>
          <div>
            <div class="loading" style="font-size: 48px; margin-bottom: 20px;">👨‍👩‍👧‍👦</div>
            <h2>Launching ${parent.displayName}'s Account...</h2>
            <p>Redirecting to parent login...</p>
          </div>
        </body>
      </html>
    `);

    // Admin-only: open a read-only preview of this parent's dashboard without
    // switching auth (true impersonation would log the admin out across tabs).
    const previewUrl = `${window.location.origin}/app/admin/parents/${encodeURIComponent(parent.id)}`;
    newWindow.location.href = previewUrl;
  };

  const convertParentToTeacher = async (parent: Parent) => {
    setConvertingParentId(parent.id);
    try {
      const defaultMuse = Object.values(PACKS)
        .flatMap(pack => pack.muses)
        .find(muse => muse.name === 'Songly') || null;
      const existingPhone = String(
        (parent as any).phoneNumber ||
        (parent as any).primaryPhone ||
        (Array.isArray(parent.phones) ? parent.phones[0] : '') ||
        ''
      ).trim();

      // Match the defaults used by the existing admin "Create Teacher" flow.
      // Parent-only fields and parent/student links are deliberately retained so
      // conversion does not destroy registration, consent, or family history.
      const teacherUpdates: Record<string, any> = {
        role: 'teacher',
        staffStatus: 'applicant',
        isAdmin: parent.isAdmin === true,
        favoriteSquadIds: Array.isArray(parent.favoriteSquadIds) ? parent.favoriteSquadIds : [],
        museCash: typeof parent.museCash === 'number' ? parent.museCash : 1000,
        selectedMuse: parent.selectedMuse || defaultMuse,
        muses: Array.isArray(parent.muses) && parent.muses.length > 0
          ? parent.muses
          : (defaultMuse ? [defaultMuse] : []),
        ...(existingPhone ? { phoneNumber: existingPhone } : {}),
      };

      await updateDoc(doc(db, 'users', parent.id), teacherUpdates);
      setParents(prev => prev.filter(item => item.id !== parent.id));
      void window.appAlert(
        `${parent.displayName || parent.email || 'The account'} is now a teacher applicant. ` +
        'Activate them from the Teachers tab when onboarding is complete.'
      );
    } catch (e) {
      console.error('Failed to convert parent to teacher', e);
      void window.appAlert('Failed to convert this parent account. Please try again.');
    } finally {
      setConvertingParentId(null);
    }
  };

  const handleOpenManualEmailModal = () => {
    setManualEmailResult(null);
    setManualEmailSquadId(selectedSquadId || '');
    setManualEmailModalOpen(true);
  };

  const handleSendManualFirstDayEmails = async () => {
    const squadId = manualEmailSquadId.trim();
    if (!squadId) {
      void window.appAlert('Please choose a squad first.');
      return;
    }
    try {
      setManualEmailSending(true);
      setManualEmailResult(null);
      const fn = httpsCallable(getFunctions(), 'sendFirstDayParentEmailsForSquad');
      const res: any = await fn({ squadId });
      const payload = (res as any)?.data || {};
      const studentsProcessed = Number(payload?.studentsProcessed || 0);
      const studentsWithRecipients = Number(payload?.studentsWithRecipients || 0);
      const emailsQueued = Number(payload?.emailsQueued || 0);
      setManualEmailResult(
        `Queued ${emailsQueued} email${emailsQueued === 1 ? '' : 's'} across ${studentsWithRecipients}/${studentsProcessed} student${studentsProcessed === 1 ? '' : 's'} in this squad.`
      );
    } catch (e: any) {
      console.error('Failed to send manual first-day parent emails', e);
      const msg = e?.message || 'Failed to send emails. Please try again.';
      setManualEmailResult(msg);
    } finally {
      setManualEmailSending(false);
    }
  };

  const exportParentsCsv = () => {
    const now = new Date();
    const dateStamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const escapeCsv = (v: any) => {
      const s = String(v ?? '');
      // Escape quotes by doubling, and wrap if it contains comma/newline/quote
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const headers = [
      'First Name',
      'Last Name',
      'Email',
      'Phone',
      'School',
      'Photo Consent',
      'Linked Students',
      'Pickup Preference',
      'Fall Linked Child',
      'Logins',
      'Last Login',
      'Next Season (Winter) Linked Child',
    ];

    const rows = filteredParents.map((p) => {
      const first = nameParts(p.displayName).first;
      const last = nameParts(p.displayName).last;
      const phone = ((p as any).primaryPhone || '') as string;
      const school = getSchoolDisplayName(((p as any).computedSchoolSlug || p.schoolSlug || '')) || '';
      const consent = ((p as any).computedPhotoConsent || p.consents?.photoRelease?.value) === 'accept'
        ? 'accept'
        : ((p as any).computedPhotoConsent || p.consents?.photoRelease?.value) === 'deny'
          ? 'deny'
          : '';
      const linked = (p.linkedStudents || []).map(s => s.displayName).join('; ');
      const pickup = String((p as any).pickupPreferenceSummary || '');
      const logins = (p as any).loginCount || 0;
      const lastLogin = formatDate(p.lastLogin);
      const fall = p.hasFallSeasonLinkedChild ? 'yes' : 'no';
      const winter = p.hasNextSeasonLinkedChild ? 'yes' : 'no';

      return [
        first,
        last,
        p.email || '',
        phone,
        school,
        consent,
        linked,
        pickup,
        fall,
        logins,
        lastLogin,
        winter,
      ];
    });

    const csv = [
      headers.map(escapeCsv).join(','),
      ...rows.map(r => r.map(escapeCsv).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `parents_export_${dateStamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="manage-parents-container">
        <div className="loading-message">Loading parents...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-parents-container">
        <div className="error-message">
          <h3>Error Loading Parents</h3>
          <p>{error}</p>
          <button onClick={loadParents} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-parents-container">
      <div className="teacher-actions" style={{ padding: '1rem 2rem 0 2rem' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button onClick={handleAddNewParent} className="add-squad-btn">
            ✨ Add New Parent
          </button>
          <button onClick={handleOpenManualEmailModal} className="add-squad-btn" title="Choose a squad and manually send first-day emails to linked parents">
            ✉️ Send First-Day Email
          </button>
          <button onClick={exportParentsCsv} className="add-squad-btn" title="Export current table to CSV for Google Sheets">
            ⬇️ Export CSV
          </button>
        </div>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label htmlFor="search">Search Parents</label>
          <input
            type="text"
            id="search"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label htmlFor="school-filter">Filter by School</label>
          <select
            id="school-filter"
            value={selectedSchool}
            onChange={(e) => setSelectedSchool(e.target.value)}
            className="filter-select"
          >
            <option value="">All Schools</option>
            {schoolOptions.map(s => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="squad-filter">Filter by Squad</label>
          <select
            id="squad-filter"
            value={selectedSquadId}
            onChange={(e) => setSelectedSquadId(e.target.value)}
            className="filter-select"
          >
            <option value="">All Squads</option>
            {visibleSquads.map(s => (
              <option key={s.id} value={s.id}>
                {s.title}{s.code ? ` (${s.code})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="linked-filter">Filter by Linked Students</label>
          <select
            id="linked-filter"
            value={linkedCountFilter}
            onChange={(e) => setLinkedCountFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All</option>
            <option value="0">No linked students</option>
            <option value="1">1 or more</option>
            <option value="2">2 or more</option>
            <option value="3">3 or more</option>
          </select>
        </div>
      </div>

      <div className="parents-table-container">
        {filteredParents.length === 0 ? (
          <div className="no-results">
            <h3>No Parents Found</h3>
            <p>No parents match your current filters.</p>
          </div>
        ) : (
          <div className="parents-table-wrapper">
            <table className="parents-table">
              <thead>
                <tr>
                  <th className="sticky-col-1 header-cell">First Name</th>
                  <th className="header-cell">Last Name</th>
                  <th className="header-cell">Email</th>
                  <th className="header-cell">Phone</th>
                  <th className="header-cell">School</th>
                  <th className="header-cell">📷</th>
                  <th className="header-cell">Linked Students</th>
                  <th className="header-cell">Pickup Preference</th>
                  <th className="header-cell">Fall</th>
                  <th className="header-cell">Winter</th>
                  <th className="header-cell">Logins</th>
                  <th className="header-cell">Last Login</th>
                  <th className="header-cell"></th>
                </tr>
              </thead>
              <tbody>
                {filteredParents.map(parent => (
                  <tr key={parent.id} className="parent-row">
                    <td className="parent-name sticky-col-1">
                      {editingCell?.id === parent.id && editingCell.field === 'firstName' ? (
                        <input
                          type="text"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={() => saveParentField(parent, 'firstName', editingValue)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveParentField(parent, 'firstName', editingValue);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          autoFocus
                          className="inline-edit-input"
                        />
                      ) : (
                        <span
                          onClick={() => beginEdit(parent, 'firstName')}
                          style={{ cursor: 'pointer' }}
                          className="editable-field"
                        >
                          {nameParts(parent.displayName).first}
                        </span>
                      )}
                    </td>
                    <td className="parent-name">
                      {editingCell?.id === parent.id && editingCell.field === 'lastName' ? (
                        <input
                          type="text"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={() => saveParentField(parent, 'lastName', editingValue)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveParentField(parent, 'lastName', editingValue);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          autoFocus
                          className="inline-edit-input"
                        />
                      ) : (
                        <span
                          onClick={() => beginEdit(parent, 'lastName')}
                          style={{ cursor: 'pointer' }}
                          className="editable-field"
                        >
                          {nameParts(parent.displayName).last}
                        </span>
                      )}
                    </td>
                    <td className="parent-email">
                      {editingCell?.id === parent.id && editingCell.field === 'email' ? (
                        <input
                          type="email"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={() => saveParentField(parent, 'email', editingValue)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveParentField(parent, 'email', editingValue);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          autoFocus
                          className="inline-edit-input"
                        />
                      ) : (
                        <span
                          onClick={() => beginEdit(parent, 'email')}
                          style={{ cursor: 'pointer' }}
                          className="editable-field"
                        >
                          {parent.email}
                        </span>
                      )}
                    </td>
                    <td className="parent-phone">
                      {editingCell?.id === parent.id && editingCell.field === 'phone' ? (
                        <input
                          type="tel"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={() => saveParentField(parent, 'phone', editingValue)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveParentField(parent, 'phone', editingValue);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          autoFocus
                          className="inline-edit-input"
                        />
                      ) : (
                        <span
                          onClick={() => beginEdit(parent, 'phone')}
                          style={{ cursor: 'pointer' }}
                          className="editable-field"
                        >
                          {(parent as any).primaryPhone || ''}
                        </span>
                      )}
                    </td>
                    <td className="parent-school">
                      {editingCell?.id === parent.id && editingCell.field === 'school' ? (
                        <select
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={() => saveParentField(parent, 'school', editingValue)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveParentField(parent, 'school', editingValue);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          autoFocus
                          className="inline-edit-select"
                        >
                          <option value="">No location</option>
                          {schoolOptions.map(s => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          onClick={() => beginEdit(parent, 'school')}
                          style={{ cursor: 'pointer' }}
                          className="editable-field"
                        >
                          {getSchoolDisplayName(((parent as any).computedSchoolSlug || parent.schoolSlug || '')) || ''}
                        </span>
                      )}
                    </td>
                    <td className="photo-consent" style={{ textAlign: 'center' }}>
                      {((parent as any).computedPhotoConsent || parent.consents?.photoRelease?.value) === 'accept'
                        ? '✅'
                        : ((parent as any).computedPhotoConsent || parent.consents?.photoRelease?.value) === 'deny'
                          ? '❌'
                          : '—'}
                    </td>
                    <td className="linked-students">
                      {parent.linkedStudents.length === 0 ? (
                        <div className="no-students-box">
                          {getUnlinkedStudentNames(parent).map((n, i) => (
                            <span key={i} className="unlinked-names">{n}</span>
                          ))}
                          <button
                            className="link-students-button"
                            onClick={() => setParentForLinking(parent)}
                            title="Link students"
                          >
                            ➕ Link Students
                          </button>
                        </div>
                      ) : (
                        <div className="students-chips">
                          {parent.linkedStudents.map(s => (
                            <button
                              key={s.id}
                              className={`student-chip ${s.isInSquad ? 'student-chip-in-squad' : 'student-chip-no-squad'}`}
                              onClick={() => setConfirm({
                                open: true,
                                title: 'Unlink student?',
                                message: `Remove ${s.displayName} from this parent?`,
                                onConfirm: () => { setConfirm({ open: false }); handleUnlink(parent.id, s.id); },
                              })}
                              title={s.isInSquad ? 'In active squad - Click to unlink' : 'Not in any active squad - Click to unlink'}
                            >
                              {s.displayName} ✕
                            </button>
                          ))}
                          <button
                            className="link-students-button small"
                            onClick={() => setParentForLinking(parent)}
                            title="Add more students"
                          >
                            ➕
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="pickup-pref">
                      {parent.linkedStudents.length === 0 ? (
                        <span>—</span>
                      ) : (
                        <div style={{ display: 'grid', gap: 8 }}>
                          {parent.linkedStudents.map((s) => {
                            const isEditing = editingPickupStudentId === s.id;
                            return (
                              <div key={s.id} style={{ display: 'grid', gap: 6, paddingBottom: 6, borderBottom: '1px solid #eef2f7' }}>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                                  <div style={{ fontWeight: 700 }}>
                                    {s.displayName}
                                    <span style={{ fontWeight: 400, marginLeft: 8 }}>
                                      {formatPickupPreference(s.pickupPreference) || '—'}
                                    </span>
                                  </div>
                                  {!isEditing ? (
                                    <button
                                      className="add-squad-btn"
                                      style={{ padding: '4px 8px', fontSize: 12 }}
                                      onClick={() => beginEditPickup(s.id, s.pickupPreference)}
                                      title="Edit pickup preference"
                                    >
                                      Edit
                                    </button>
                                  ) : null}
                                </div>

                                {isEditing && (
                                  <div style={{ display: 'grid', gap: 6 }}>
                                    <div style={{ display: 'grid', gap: 4 }}>
                                      {Object.entries(PICKUP_LABELS).map(([key, label]) => (
                                        <label key={key} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                                          <input
                                            type="checkbox"
                                            checked={pickupDraftOptions.includes(key)}
                                            onChange={() => togglePickupDraftOption(key)}
                                            disabled={savingPickup}
                                          />
                                          <span>{label}</span>
                                        </label>
                                      ))}
                                    </div>
                                    {pickupDraftOptions.includes('other') && (
                                      <input
                                        type="text"
                                        value={pickupDraftOtherText}
                                        onChange={(e) => setPickupDraftOtherText(e.target.value)}
                                        placeholder="Other (please specify)"
                                        disabled={savingPickup}
                                        style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 12 }}
                                      />
                                    )}
                                    <div style={{ display: 'flex', gap: 8 }}>
                                      <button
                                        className="add-squad-btn"
                                        style={{ padding: '4px 8px', fontSize: 12, background: '#16a34a' }}
                                        onClick={() => savePickupForStudent(s.id)}
                                        disabled={savingPickup}
                                      >
                                        {savingPickup ? 'Saving…' : 'Save'}
                                      </button>
                                      <button
                                        className="add-squad-btn"
                                        style={{ padding: '4px 8px', fontSize: 12, background: '#1f2937' }}
                                        onClick={() => cancelEditPickup()}
                                        disabled={savingPickup}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td className="fall-flag" style={{ textAlign: 'center' }} title="Any linked child enrolled in a Fall term squad (including archived)">
                      {parent.hasFallSeasonLinkedChild ? '✅' : '—'}
                    </td>
                    <td className="winter-flag" style={{ textAlign: 'center' }} title="Any linked child enrolled in a Winter term squad (including archived)">
                      {parent.hasNextSeasonLinkedChild ? '✅' : '—'}
                    </td>
                    <td className="login-count" title="Total number of logins recorded">
                      {(parent as any).loginCount || 0}
                    </td>
                    <td className="last-login">
                      {formatDate(parent.lastLogin)}
                    </td>
                    <td className="actions" style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          className="add-squad-btn"
                          style={{ padding: '6px 12px', fontSize: '0.85rem', background: '#16a34a' }}
                          title={`Launch ${parent.displayName}'s account`}
                          onClick={() => handleLaunchParentAccount(parent)}
                        >
                          LAUNCH
                        </button>
                        <button
                          className="add-squad-btn"
                          style={{ padding: '6px 12px', fontSize: '0.85rem', background: '#2563eb' }}
                          title={`Convert ${parent.displayName || parent.email || 'this parent'} to a teacher`}
                          disabled={convertingParentId === parent.id}
                          onClick={() => setConfirm({
                            open: true,
                            title: 'Convert Parent to Teacher',
                            message:
                              `Convert ${parent.displayName || parent.email || 'this account'} to a teacher applicant? ` +
                              'They will sign in through the teacher experience and will no longer have parent-dashboard access. ' +
                              'Existing child links and parent registration data will be retained.',
                            onConfirm: async () => {
                              setConfirm({ open: false });
                              await convertParentToTeacher(parent);
                            },
                          })}
                        >
                          {convertingParentId === parent.id ? 'CONVERTING…' : 'MAKE TEACHER'}
                        </button>
                        <button
                          className="delete-btn"
                          title={`Delete ${parent.displayName}`}
                          onClick={() => setConfirm({
                            open: true,
                            title: 'Delete Parent',
                            message: `Are you sure you would like to permanently delete ${parent.displayName}? This will also remove their login.`,
                            onConfirm: async () => {
                              setConfirm({ open: false });
                              try {
                                const deleteParent = httpsCallable(getFunctions(), 'adminDeleteParentCompletely');
                                await deleteParent({ uid: parent.id });
                                await loadParents();
                              } catch (e: any) {
                                console.error('[ManageParents] Failed to delete parent completely:', e);
                                void window.appAlert(e?.message || 'Failed to delete parent');
                              }
                            }
                          })}
                        >
                          DELETE
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {parentForLinking && (
        <LinkStudentsModal
          parent={{ id: parentForLinking.id, displayName: parentForLinking.displayName || undefined, email: parentForLinking.email || undefined }}
          existingStudentIds={parentForLinking.linkedStudents.map(s => s.id)}
          onClose={() => setParentForLinking(null)}
          onLinked={() => { setParentForLinking(null); loadParents(); }}
        />
      )}

      <ConfirmDialog
        open={confirm.open}
        title={confirm.title}
        message={confirm.message}
        onCancel={() => setConfirm({ open: false })}
        onConfirm={() => confirm.onConfirm ? confirm.onConfirm() : setConfirm({ open: false })}
      />

      {creatingParent && (
        <ParentInfoModal
          parent={creatingParent}
          onClose={() => setCreatingParent(null)}
          onParentUpdated={() => { setCreatingParent(null); loadParents(); }}
        />
      )}

      {manualEmailModalOpen && (
        <div className="manual-email-modal-overlay" onClick={() => !manualEmailSending && setManualEmailModalOpen(false)}>
          <div className="manual-email-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manual-email-modal-header">
              <h3>Send first-day parent emails</h3>
              <button
                className="manual-email-close-btn"
                onClick={() => !manualEmailSending && setManualEmailModalOpen(false)}
                disabled={manualEmailSending}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <p className="manual-email-help-text">
              Pick a squad. We will send individualized first-day emails to linked parent accounts for students in that squad.
            </p>
            <div className="manual-email-field">
              <label htmlFor="manual-email-squad">Squad</label>
              <select
                id="manual-email-squad"
                value={manualEmailSquadId}
                onChange={(e) => setManualEmailSquadId(e.target.value)}
                disabled={manualEmailSending}
              >
                <option value="">Select a squad...</option>
                {visibleSquads.map((s) => (
                  <option key={s.id} value={s.id}>
                    {String(s.title || 'Untitled Squad')}
                    {(s as any).schoolName ? ` • ${(s as any).schoolName}` : ''}
                    {(s as any).status === 'archived' ? ' (Archived)' : ''}
                  </option>
                ))}
              </select>
            </div>
            {manualEmailResult && <div className="manual-email-result">{manualEmailResult}</div>}
            <div className="manual-email-actions">
              <button
                className="add-squad-btn"
                style={{ background: '#1f2937' }}
                onClick={() => setManualEmailModalOpen(false)}
                disabled={manualEmailSending}
              >
                Cancel
              </button>
              <button
                className="add-squad-btn"
                onClick={handleSendManualFirstDayEmails}
                disabled={manualEmailSending || !manualEmailSquadId}
              >
                {manualEmailSending ? 'Sending…' : 'Send Emails'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageParents;
